import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { AzureHorizonDBSink } from 'app/model/pipeline/copysink/azure-horizon-db.copysink';
import { TabularSinkAdapter } from './tabular.sink.adapter';
import { ValueAdapter } from '../../value.adapter';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';
import { SharedModel } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

// Azure HorizonDB is PostgreSQL wire-compatible, so it reuses the exact same copy-sink
// serialization/behavior (including write-method options) as Azure Database for PostgreSQL; only
// the emitted copy-sink type differs ('AzureHorizonDBSink').
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureHorizonDBSink),
    asynchronous: true,
    adapts: {
        type: '*',
        writeBatchTimeout: '*',
        writeBatchSize: '*',
        preCopyScript: '*',
        writeMethod: '*',
        upsertSettings: '*'
    }
})
export class AzureHorizonDBSinkAdapter extends CopySinkAdapter<AzureHorizonDBSink, Model.AzurePostgreSQLSink> {
    protected override async _toResource(model: AzureHorizonDBSink): Promise<Model.AzurePostgreSQLSink> {
        const result: Model.AzurePostgreSQLSink = {
            type: adfModelTypeMap.azureHorizonDb.copySinkType
        };
        TabularSinkAdapter.toSinkResource(model, result);
        result.writeMethod = ValueAdapter.toResource(model, AzureHorizonDBSink.WRITE_METHOD) || undefined;
        if (result.writeMethod === 'Upsert') {
            this._upsertToResource(model, result);
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.AzurePostgreSQLSink,
        owner: Activity
    ): Promise<AzureHorizonDBSink> {
        const result = Owned.create<AzureHorizonDBSink>(Metamodel.modelFor(AzureHorizonDBSink), owner);
        TabularSinkAdapter.toSinkModel(resource, result);
        result.writeMethod = undefined;
        ValueAdapter.toModel(result, AzureHorizonDBSink.WRITE_METHOD, resource.writeMethod);
        if (resource.writeMethod === 'Upsert') {
            this._upsertToModel(resource, result);
        }
        return result;
    }

    private _upsertToResource(model: AzureHorizonDBSink, result: Model.AzurePostgreSQLSink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        const upsertSettings = ValueAdapter.toResource(model, AzureHorizonDBSink.UPSERT_SETTINGS);
        if (upsertSettings?.type === 'Expression') {
            result.upsertSettings = upsertSettings;
        } else {
            result.upsertSettings = {
                keys: ValueAdapter.toResource(model, AzureHorizonDBSink.WRITE_BEHAVIOR_KEYS)
            };
        }
    }

    private _upsertToModel(resource: Model.AzurePostgreSQLSink, result: AzureHorizonDBSink): void {
        if (resource.upsertSettings) {
            // for copy-wizard generated activities, where upsertSettings parameterized as one
            if ((resource.upsertSettings as SharedModel.ExpressionValue)?.type === 'Expression') {
                ValueAdapter.toModel(result, AzureHorizonDBSink.UPSERT_SETTINGS, resource.upsertSettings);
            } else {
                const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
                ValueAdapter.toModel(result, AzureHorizonDBSink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
            }
        }
    }
}
